import React, { useEffect, useState } from 'react'
import jwt_decode from 'jwt-decode'
import { QRCodeSVG } from 'qrcode.react'
import TitleCard from '../components/TitleCard'
import FiltersAddBar from '../components/FiltersAddBar'
import UserListItem from '../components/UserListItem'
import Modal, {
  ConfirmModal,
  AlertModal,
  DoubleConfirmModal,
} from '../components/Modal'

const TITLE_MAX = 80
const BODY_MAX = 240

function buildQrUrl(token) {
  const origin =
    typeof window !== 'undefined' && window.location && window.location.origin
      ? window.location.origin
      : 'https://app.cutcatering.it'
  return `${origin}/qr-login?token=${encodeURIComponent(token)}`
}

function formatExpiry(iso) {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    return d.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch (e) {
    return iso
  }
}

function authHeaders() {
  const token = localStorage.getItem('cut-catering-token')
  return token
    ? { 'Content-Type': 'application/json', 'x-auth-token': token }
    : { 'Content-Type': 'application/json' }
}

function currentUserIdFromToken() {
  try {
    const t = localStorage.getItem('cut-catering-token')
    if (!t) return ''
    const decoded = /** @type {any} */ (jwt_decode(t))
    return (decoded && decoded._id) || ''
  } catch (e) {
    return ''
  }
}

function Users(props) {
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('Tutti')
  const [orders, setOrders] = useState([])
  const [currentUserId] = useState(currentUserIdFromToken)

  // Reset password confirm + alert
  const [resetTarget, setResetTarget] = useState(null) // {email, name}
  const [resetAlert, setResetAlert] = useState('')

  // Test push state
  const [pushTarget, setPushTarget] = useState(null) // user object
  const [pushTitle, setPushTitle] = useState('Notifica di test')
  const [pushBody, setPushBody] = useState('Ciao! Questa è una notifica di prova da CUT Catering.')
  const [pushSending, setPushSending] = useState(false)
  const [pushError, setPushError] = useState('')
  const [pushAlert, setPushAlert] = useState('')

  // QR login modal state
  const [qrTarget, setQrTarget] = useState(null) // user object
  const [qrLoading, setQrLoading] = useState(false)
  const [qrToken, setQrToken] = useState('')
  const [qrExpiresAt, setQrExpiresAt] = useState('')
  const [qrError, setQrError] = useState('')
  const [qrCopied, setQrCopied] = useState(false)

  // Toggle abilita/disabilita
  const [toggleTarget, setToggleTarget] = useState(null) // user object (vuole disabilitarlo)
  const [toggleEnableTarget, setToggleEnableTarget] = useState(null) // user object (vuole riabilitarlo)
  const [toggleAlert, setToggleAlert] = useState('')

  // Delete (doppia conferma)
  const [deleteTarget, setDeleteTarget] = useState(null) // user object
  const [deleteAlert, setDeleteAlert] = useState('')

  useEffect(() => {
    fetch('/api/users/', {
      method: 'GET',
      headers: authHeaders(),
    })
      .then(response => response.json())
      .then(result => setOrders(result))
      .catch(error => console.log('error', error))
  }, [])

  const filterOptions = ['Tutti', 'Producers', 'Singoli']

  let getOrders = function () {
    return orders
      .filter(e => {
        if (!search || (e.customer || '').toLowerCase().includes(search.toLowerCase()))
          return true
        if (!search || (e.name || '').toLowerCase().includes(search.toLowerCase()))
          return true
        return false
      })
      .filter(e => {
        if (
          filter === 'Tutti' ||
          (filter === 'Singoli' && e.type === 'single') ||
          (filter === 'Producers' && e.type === 'producer')
        )
          return true
        return false
      })
  }

  const onResetConfirm = async () => {
    const email = resetTarget?.email
    setResetTarget(null)
    if (!email) return
    try {
      const r = await fetch(
        '/api/users/reset-password/' + encodeURIComponent(email),
        { method: 'POST', headers: authHeaders() },
      )
      if (r.status === 204 || r.ok) {
        setResetAlert(
          `Mail di reset inviata a ${email} (se l'indirizzo è valido). Il link scade tra 60 minuti.`,
        )
      } else {
        setResetAlert('Impossibile inviare la mail di reset. Riprova più tardi.')
      }
    } catch (e) {
      setResetAlert('Impossibile inviare la mail di reset. Riprova più tardi.')
    }
  }

  const generateQr = async user => {
    setQrError('')
    setQrCopied(false)
    setQrLoading(true)
    try {
      const r = await fetch(
        '/api/admin/qr-login/' + encodeURIComponent(user._id),
        { method: 'POST', headers: authHeaders() },
      )
      if (!r.ok) {
        const t = await r.text().catch(() => '')
        setQrError(t || 'Errore durante la generazione del QR.')
        setQrToken('')
        setQrExpiresAt('')
        return
      }
      const data = await r.json()
      setQrToken(data.token)
      setQrExpiresAt(data.expiresAt)
    } catch (e) {
      setQrError('Impossibile contattare il server.')
      setQrToken('')
      setQrExpiresAt('')
    } finally {
      setQrLoading(false)
    }
  }

  const openQrFor = async user => {
    setQrTarget(user)
    setQrToken('')
    setQrExpiresAt('')
    setQrError('')
    setQrCopied(false)
    await generateQr(user)
  }

  const closeQr = () => {
    if (qrLoading) return
    setQrTarget(null)
    setQrToken('')
    setQrExpiresAt('')
    setQrError('')
    setQrCopied(false)
  }

  const setUserEnabled = async (user, makeEnabled) => {
    // Marker `"disabled"` per spegnere; valore vuoto per riabilitare. Il
    // backend (filter `$ne:"disabled"`) e la UI (`enabled !== "disabled"`)
    // riconoscono solo questo marker — i `false` legacy sono ignorati.
    const newValue = makeEnabled ? '' : 'disabled'
    try {
      const r = await fetch('/api/users/' + encodeURIComponent(user._id), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ enabled: newValue }),
      })
      if (!r.ok) {
        const t = await r.text().catch(() => '')
        setToggleAlert(t || `Operazione non riuscita (${r.status}).`)
        return
      }
      // Aggiorna in-place per riflettere lo stato senza ricaricare tutto.
      setOrders(prev =>
        prev.map(u => (u._id === user._id ? { ...u, enabled: newValue } : u)),
      )
      setToggleAlert(
        makeEnabled
          ? `${user.name || user.email} è stato riabilitato.`
          : `${user.name || user.email} è stato disabilitato.`,
      )
    } catch (e) {
      setToggleAlert('Impossibile contattare il server.')
    }
  }

  const onConfirmDisable = async () => {
    const target = toggleTarget
    setToggleTarget(null)
    if (target) await setUserEnabled(target, false)
  }

  const onConfirmEnable = async () => {
    const target = toggleEnableTarget
    setToggleEnableTarget(null)
    if (target) await setUserEnabled(target, true)
  }

  const onConfirmDelete = async () => {
    const target = deleteTarget
    setDeleteTarget(null)
    if (!target) return
    try {
      const r = await fetch('/api/users/' + encodeURIComponent(target._id), {
        method: 'DELETE',
        headers: authHeaders(),
      })
      if (!r.ok) {
        const t = await r.text().catch(() => '')
        setDeleteAlert(t || `Eliminazione non riuscita (${r.status}).`)
        return
      }
      setOrders(prev => prev.filter(u => u._id !== target._id))
      setDeleteAlert(`${target.name || target.email} è stato eliminato.`)
    } catch (e) {
      setDeleteAlert('Impossibile contattare il server.')
    }
  }

  const copyQrLink = async () => {
    if (!qrToken) return
    const url = buildQrUrl(qrToken)
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url)
        setQrCopied(true)
        setTimeout(() => setQrCopied(false), 2000)
      }
    } catch (e) {
      /* silent — fallback: l'utente può selezionare manualmente */
    }
  }

  const onSendTestPush = async () => {
    if (pushSending) return
    const title = pushTitle.trim()
    const body = pushBody.trim()
    if (!title || !body) {
      setPushError('Compila titolo e messaggio.')
      return
    }
    setPushError('')
    setPushSending(true)
    try {
      const r = await fetch(
        '/api/admin/notify/test/' + encodeURIComponent(pushTarget._id),
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ title, body }),
        },
      )
      if (r.ok) {
        const data = await r.json()
        setPushTarget(null)
        setPushAlert(
          `Notifica inviata: ${data.sent}/${data.subscriptionsCount} dispositivi raggiunti${
            data.removed ? `, ${data.removed} subscription scadute rimosse` : ''
          }.`,
        )
      } else if (r.status === 409) {
        setPushError(
          "L'utente non ha ancora attivato le notifiche su nessun dispositivo.",
        )
      } else if (r.status === 503) {
        setPushError('Il servizio di notifiche push non è configurato sul server.')
      } else {
        const t = await r.text().catch(() => '')
        setPushError(t || 'Errore durante l\'invio.')
      }
    } catch (e) {
      setPushError('Impossibile contattare il server.')
    } finally {
      setPushSending(false)
    }
  }

  return (
    <div className="dashboard-container">
      <div className="column">
        <TitleCard title="Utenti" subtitle={`Tutti gli utenti registrati!`} />
      </div>
      <div className="column">
        <FiltersAddBar
          search={search}
          setSearch={setSearch}
          filter={filter}
          setFilter={setFilter}
          newPath="/users/new"
          filterOptions={filterOptions}
        />
      </div>
      <div className="column-wrap">
        <div className="column-100 column-child-lg-50">
          {getOrders().map((el, key) => (
            <div key={key} className="col-element">
              <UserListItem
                user={el}
                currentUserId={currentUserId}
                resetPassword={() =>
                  setResetTarget({ email: el.email, name: el.name })
                }
                onTestPush={() => {
                  setPushError('')
                  setPushTitle('Notifica di test')
                  setPushBody(
                    `Ciao ${el.name || ''}! Questa è una notifica di prova da CUT Catering.`,
                  )
                  setPushTarget(el)
                }}
                onShowQr={() => openQrFor(el)}
                onToggleEnabled={user => {
                  if (user.enabled === 'disabled') setToggleEnableTarget(user)
                  else setToggleTarget(user)
                }}
                onDelete={user => setDeleteTarget(user)}
              />
            </div>
          ))}
        </div>
      </div>

      <ConfirmModal
        open={!!resetTarget}
        title="Inviare la mail di reset password?"
        message={
          resetTarget
            ? `Verrà inviata una mail con un link per reimpostare la password a ${resetTarget.email}. Il link scadrà tra 60 minuti.`
            : ''
        }
        confirmLabel="Invia mail"
        cancelLabel="Annulla"
        onConfirm={onResetConfirm}
        onCancel={() => setResetTarget(null)}
      />

      <AlertModal
        open={!!resetAlert}
        title="Reset password"
        message={resetAlert}
        onClose={() => setResetAlert('')}
      />

      <AlertModal
        open={!!pushAlert}
        title="Notifica inviata"
        message={pushAlert}
        onClose={() => setPushAlert('')}
      />

      <ConfirmModal
        open={!!toggleTarget}
        title="Disabilitare l'utente?"
        message={
          toggleTarget
            ? `${toggleTarget.name || toggleTarget.email} non potrà più ordinare né effettuare login finché non lo riabiliti. Le notifiche push non gli verranno più inviate. L'operazione è reversibile in qualsiasi momento.`
            : ''
        }
        confirmLabel="Disabilita utente"
        cancelLabel="Annulla"
        danger
        onConfirm={onConfirmDisable}
        onCancel={() => setToggleTarget(null)}
      />

      <ConfirmModal
        open={!!toggleEnableTarget}
        title="Riabilitare l'utente?"
        message={
          toggleEnableTarget
            ? `${toggleEnableTarget.name || toggleEnableTarget.email} potrà di nuovo ordinare e accedere all'app.`
            : ''
        }
        confirmLabel="Riabilita utente"
        cancelLabel="Annulla"
        onConfirm={onConfirmEnable}
        onCancel={() => setToggleEnableTarget(null)}
      />

      <AlertModal
        open={!!toggleAlert}
        title="Stato utente aggiornato"
        message={toggleAlert}
        onClose={() => setToggleAlert('')}
      />

      <DoubleConfirmModal
        open={!!deleteTarget}
        title="Elimina utente — operazione irreversibile"
        message={
          deleteTarget
            ? `Stai per eliminare definitivamente ${deleteTarget.name || deleteTarget.email} dal database. Lo storico ordini resterà ma l'utente non potrà più accedere e dovrà essere ricreato manualmente per tornare a usare la piattaforma. Se vuoi solo sospendere temporaneamente l'utente, usa "Disabilita" invece.`
            : ''
        }
        confirmKeyword={
          deleteTarget ? deleteTarget.name || deleteTarget.email : ''
        }
        hint={
          deleteTarget ? (
            <>
              Per confermare, scrivi{' '}
              <b>{deleteTarget.name || deleteTarget.email}</b>
            </>
          ) : null
        }
        confirmLabel="Elimina definitivamente"
        cancelLabel="Annulla"
        onConfirm={onConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <AlertModal
        open={!!deleteAlert}
        title="Utente eliminato"
        message={deleteAlert}
        onClose={() => setDeleteAlert('')}
      />

      <Modal open={!!qrTarget} onClose={closeQr}>
        <h2 className="modal-title">
          QR di accesso rapido
          {qrTarget ? ` — ${qrTarget.name || qrTarget.email}` : ''}
        </h2>
        <p className="modal-message" style={{ marginBottom: 12 }}>
          L'utente può scansionare questo QR dall'app o aprire il link su uno
          smartphone per accedere senza password. Il QR è{' '}
          <b>monouso</b>: dopo la prima scansione viene bruciato e non
          funziona più.
        </p>

        {qrLoading ? (
          <p style={{ textAlign: 'center', color: '#666', padding: '24px 0' }}>
            Generazione QR…
          </p>
        ) : qrError ? (
          <>
            <p className="modal-error">{qrError}</p>
            <div className="modal-actions">
              <button
                type="button"
                className="modal-btn modal-btn-secondary"
                onClick={closeQr}
              >
                Chiudi
              </button>
              <button
                type="button"
                className="modal-btn modal-btn-primary"
                onClick={() => qrTarget && generateQr(qrTarget)}
              >
                Riprova
              </button>
            </div>
          </>
        ) : qrToken ? (
          <>
            <div className="qr-display">
              <QRCodeSVG
                value={buildQrUrl(qrToken)}
                size={232}
                level="M"
                includeMargin
              />
            </div>
            <div className="qr-meta">
              <div className="qr-meta-row">
                <span className="qr-meta-label">Valido fino al</span>
                <span className="qr-meta-value">
                  {formatExpiry(qrExpiresAt)}
                </span>
              </div>
              <div className="qr-meta-row qr-meta-row-link">
                <span className="qr-meta-label">Link</span>
                <code className="qr-meta-link">{buildQrUrl(qrToken)}</code>
              </div>
            </div>
            <div className="modal-actions" style={{ flexWrap: 'wrap' }}>
              <button
                type="button"
                className="modal-btn modal-btn-secondary"
                onClick={() => qrTarget && generateQr(qrTarget)}
              >
                Rigenera
              </button>
              <button
                type="button"
                className="modal-btn modal-btn-secondary"
                onClick={copyQrLink}
              >
                {qrCopied ? 'Copiato!' : 'Copia link'}
              </button>
              <button
                type="button"
                className="modal-btn modal-btn-primary"
                onClick={closeQr}
              >
                Chiudi
              </button>
            </div>
            <p className="modal-help" style={{ marginTop: 14 }}>
              Puoi inviare il link via WhatsApp o email. Anche in caso di
              screenshot, dopo il primo accesso il QR è invalidato e nessun
              altro può usarlo.
            </p>
          </>
        ) : null}
      </Modal>

      <Modal
        open={!!pushTarget}
        onClose={() => !pushSending && setPushTarget(null)}
      >
        <h2 className="modal-title">
          Invia notifica di test {pushTarget ? `a ${pushTarget.name || pushTarget.email}` : ''}
        </h2>
        <p className="modal-message" style={{ marginBottom: 12 }}>
          Userà la notifica push del browser. L'utente deve aver già attivato le
          notifiche dal proprio dispositivo dalle impostazioni dell'app.
        </p>
        <div className="modal-field">
          <label className="modal-label">
            Titolo
            <span className="modal-counter">
              {pushTitle.length}/{TITLE_MAX}
            </span>
          </label>
          <input
            className="modal-input"
            type="text"
            value={pushTitle}
            maxLength={TITLE_MAX}
            disabled={pushSending}
            onChange={e => setPushTitle(e.target.value)}
          />
        </div>
        <div className="modal-field">
          <label className="modal-label">
            Messaggio
            <span className="modal-counter">
              {pushBody.length}/{BODY_MAX}
            </span>
          </label>
          <textarea
            className="modal-textarea"
            rows={4}
            value={pushBody}
            maxLength={BODY_MAX}
            disabled={pushSending}
            onChange={e => setPushBody(e.target.value)}
          />
        </div>
        {pushError && (
          <p className="modal-error">{pushError}</p>
        )}
        <div className="modal-actions">
          <button
            type="button"
            className="modal-btn modal-btn-secondary"
            onClick={() => setPushTarget(null)}
            disabled={pushSending}
          >
            Annulla
          </button>
          <button
            type="button"
            className="modal-btn modal-btn-primary"
            onClick={onSendTestPush}
            disabled={pushSending}
          >
            {pushSending ? 'Invio...' : 'Invia notifica'}
          </button>
        </div>
      </Modal>
    </div>
  )
}

export default Users
